import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ShieldCheck, FileText, Activity, Users, BarChart3, Briefcase, 
  ArrowRight, ChevronRight, CheckCircle2, 
  FileCheck, AlertCircle, Lock, Target
} from 'lucide-react';
import Insurance from '../../assets/Insurance.png'; // Assuming you have an image in assets

const CONTAINER = 'w-full max-w-7xl xl:max-w-[95%] 2xl:max-w-[85vw] mx-auto px-6 sm:px-8 lg:px-8';
const THEME_COLOR = '#10b981'; // Emerald/Green theme

/* ============================== MOCKUP COMPONENTS ============================== */

// const BrandMark = ({ variant = 'gradient', imgClass = 'h-4', textClass = 'text-[10px]' }) => (
//   <span className="inline-flex items-center gap-1 leading-none">
//     <img src="/smartedzmain.png" alt="SmartEdz" className={`${imgClass} w-auto object-contain`} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
//     <span className={`${textClass} font-bold tracking-tight leading-none`}>
//       {variant === 'white' ? <span className="text-white">SmartEdz</span> : (
//         <><span className="bg-gradient-to-t from-[#18469A] via-[#1B83C5] to-[#21BEE1] bg-clip-text text-transparent">Smart</span><span className="bg-gradient-to-t from-[#E35D14] to-[#F4A51D] bg-clip-text text-transparent">Edz</span></>
//       )}
//     </span>
//   </span>
// );

/* ============================== PAGE DATA ============================== */

const FEATURES = [
  { title: 'Policy Management', icon: FileText, desc: 'Manage end-to-end policy lifecycles, track renewals, and streamline underwriting processes seamlessly.' },
  { title: 'Claims Processing', icon: Activity, desc: 'Automate claims intake, validation, and settlement workflows to reduce turnaround time.' },
  { title: 'Customer Management', icon: Users, desc: 'Maintain 360-degree customer profiles, communication history, and overall policy portfolios.' },
  { title: 'Reports & Analytics', icon: BarChart3, desc: 'Generate real-time insights on risk assessment, premium collection, and agency performance.' },
  { title: 'Agent Portal', icon: Briefcase, desc: 'Empower your agents with a dedicated portal for tracking commissions, leads, and sales targets.' },
  { title: 'Compliance & Security', icon: ShieldCheck, desc: 'Ensure regulatory adherence with built-in audit trails and highly secure data management.' }
];

const InsuranceERP = () => {
  return (
    <div className="overflow-x-hidden pt-0">
      <style>{`
        @keyframes seFadeUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
        .se-fade-up { animation: seFadeUp .7s ease-out both; }
        @keyframes seFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-12px)} }
        .se-float { animation: seFloat 6s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) { .se-float, .se-fade-up { animation: none !important; } }
      `}</style>

      {/* ================= HERO SECTION ================= */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#ecfdf5] via-[#f8fafc] to-white">
        <div className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="pointer-events-none absolute top-40 -left-24 w-80 h-80 rounded-full bg-[#10b981]/10 blur-3xl" />
        <div className={`${CONTAINER} relative py-5 lg:py-6`}>
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-8 items-center">
            
            <div className="max-w-2xl relative z-10 se-fade-up">
              <nav className="flex flex-wrap items-center gap-2 text-sm text-slate-500 mb-6">
                <Link to="/" className="hover:text-primary transition-colors">Home</Link>
                <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                <Link to="/industries" className="hover:text-primary transition-colors">Industries</Link>
                <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-primary font-medium">Insurance ERP</span>
              </nav>

              <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.18em] uppercase text-primary bg-white ring-1 ring-primary/25 px-4 py-1.5 rounded-full mb-6">
                <ShieldCheck className="w-4 h-4" /> SmartEdz Insurance Solutions
              </span>
              <h1 className="text-4xl sm:text-5xl xl:text-[3.7rem] font-extrabold leading-[1.08] tracking-tight mb-6" style={{ color: '#13294b' }}>
                Complete Insurance & Agency Management
              </h1>
              <p className="text-lg text-slate-500 max-w-xl leading-relaxed mb-8">
                Streamline your insurance operations. Manage policies, accelerate claims processing, and boost agent productivity with our specialized ERP platform.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/request-demo" className="inline-flex items-center gap-2 rounded-lg px-7 py-3.5 font-semibold text-white shadow-lg shadow-primary/30 bg-primary hover:brightness-110 transition">
                  Request a Demo <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* Replaced Mockups with Provided Image */}
            <div className="relative se-fade-up w-full" style={{ animationDelay: '.15s' }}>
              <div className="absolute inset-x-8 top-4 bottom-16 bg-gradient-to-tr from-[#10b981]/15 to-[#34d399]/15 blur-3xl rounded-[48px]" />
              <img 
                src={Insurance} 
                alt="Insurance ERP Dashboard Illustration" 
                className="relative z-10 w-full h-auto object-contain mix-blend-multiply hover:scale-[1.02] transition-transform duration-500"
              />
            </div>

          </div>
        </div>
      </section>

      {/* ================= CORE MODULES ================= */}
      <section className="bg-gradient-to-b from-[#f5f9fd] to-white py-14">
        <div className={CONTAINER}>
          <div className="text-center mb-10">
            <span className="block text-xs font-bold tracking-[0.2em] uppercase text-primary mb-3">Core Modules</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ color: '#13294b' }}>Everything you need in one dashboard</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((feature, index) => (
              <div key={index} className="group h-full flex flex-col bg-white rounded-2xl ring-1 ring-slate-100 p-6 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110" style={{ backgroundColor: `${THEME_COLOR}1A` }}>
                  <feature.icon className="w-6 h-6" style={{ color: THEME_COLOR }} />
                </div>
                <h3 className="font-bold text-slate-800 text-lg mb-3">{feature.title}</h3>
                <p className="text-[13px] text-slate-500 leading-relaxed flex-1">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= INDUSTRY BENEFITS ================= */}
      <section className="py-14 bg-white">
        <div className={CONTAINER}>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="se-fade-up">
              <span className="block text-xs font-bold tracking-[0.2em] uppercase text-[#10b981] mb-3">
                Why SmartEdz?
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-6" style={{ color: '#13294b' }}>
                Built for strict compliance and automated claims
              </h2>
              <p className="text-slate-500 mb-8 leading-relaxed">
                Generic CRM tools lack the rigid security and specialized workflows required for insurance. Our platform is engineered to handle complex underwriting rules, fraud detection, and multi-tier agent commissions effortlessly.
              </p>
              <div className="space-y-6">
                {[
                  { title: 'Automated Underwriting', desc: 'Accelerate policy issuance with rule-based risk assessment algorithms that flag anomalies instantly.', icon: FileCheck },
                  { title: 'Fraud & Risk Detection', desc: 'Leverage built-in analytics to detect duplicate claims and unusual patterns before settlement.', icon: AlertCircle },
                  { title: 'Bank-Grade Security', desc: 'Secure sensitive customer health and financial data with enterprise-level encryption and access controls.', icon: Lock }
                ].map((item, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 bg-[#10b981]/10 text-[#10b981]">
                      <item.icon className="w-6 h-6" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-800 text-lg">{item.title}</h4>
                      <p className="text-sm text-slate-500 mt-1">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative rounded-2xl bg-slate-50 border border-slate-100 p-8 shadow-inner se-fade-up" style={{ animationDelay: '0.2s' }}>
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#10b981]/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
              <h3 className="font-bold text-slate-800 mb-6 flex items-center gap-2">
                <Target className="w-5 h-5 text-[#10b981]" /> Agent Performance
              </h3>
              <div className="space-y-4 relative z-10">
                {['Quarterly Targets Met', 'Commission Payouts Cleared', 'Compliance Training Updated'].map((label, idx) => (
                  <div key={idx} className="bg-white p-4 rounded-xl shadow-sm border border-slate-100 flex items-center justify-between">
                    <span className="font-semibold text-slate-700 text-sm">{label}</span>
                    <span className="flex items-center gap-1 text-xs font-bold text-[#10b981] bg-[#10b981]/10 px-2 py-1 rounded-md">
                      <CheckCircle2 className="w-3 h-3" /> Verified
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= BOTTOM CTA ================= */}
      <section className="py-16">
        <div className={CONTAINER}>
          <div className="relative rounded-[2rem] overflow-hidden bg-gradient-to-br from-[#13294b] to-[#1e447a] p-10 sm:p-16 text-center">
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#10b981]/20 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3" />
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#10b981]/10 rounded-full blur-3xl -translate-x-1/3 translate-y-1/3" />
            
            <div className="relative z-10 max-w-2xl mx-auto">
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">
                Ready to digitize your insurance agency?
              </h2>
              <p className="text-slate-300 text-lg mb-8">
                Join modern insurance providers who use SmartEdz to reduce claim turnaround time, ensure data security, and scale their broker networks.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <Link to="/request-demo" className="inline-flex items-center gap-2 rounded-lg px-8 py-4 font-bold text-[#13294b] bg-white hover:bg-slate-50 transition shadow-lg hover:-translate-y-1">
                  Schedule a Free Demo <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/contact-us" className="inline-flex items-center gap-2 rounded-lg px-8 py-4 font-bold text-white bg-white/10 hover:bg-white/20 transition ring-1 ring-white/20">
                  Talk to Sales
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InsuranceERP;